"use client";

import { useCallback, useState } from "react";

import { useToast } from "@/components/providers/FetchToastProvider";

import { useEstimator } from "../_context/EstimatorContext";
import { readAttendeeDocument } from "../_logic/attendee-document";
import { askToConfirm } from "../_logic/attendee-upload-store";
import {
  aggregateHomeCities,
  findCountColumn,
  looksLikeHeader,
  pickCityColumn,
  placeColumn,
  placeQuery,
  rowPeople,
  sampleValues,
  type AttendeeAggregate,
} from "../_logic/attendees";
import { ATTENDEE_COLUMN_SAMPLE, MAX_ATTENDEE_FILE_BYTES, MAX_ORIGIN_COUNT } from "../_logic/constants";
import { resolvePlaces } from "../_logic/place-catalog";
import type { PlaceOption } from "../_logic/places";
import { isAttendeeDocument, readAttendeeRows } from "../_logic/read-attendee-file";
import type { Origin } from "../_logic/types";

/** Unmatched entries named in the toast before "and N more". */
const UNMATCHED_NAMED = 3;

/** A refusal the planner should read as is, rather than a generic failure. */
class UploadRefused extends Error {}

/**
 * Whether a sheet value names the place it resolved to: a city or alias, or an airport code
 * written as one ("SFO"). A person's name that happens to be a code ("Ada") doesn't count.
 */
const namesPlace = (value: string, place: PlaceOption | null): boolean =>
  place !== null &&
  (place.match === "exact" || place.match === "alias" || (place.match === "code" && /^[A-Z]{3}$/.test(value.trim())));

/** Resolves sheet values to places, all in the browser. */
const resolveAll = async (names: string[]): Promise<Map<string, PlaceOption | null>> => {
  const results = await resolvePlaces(names.map(placeQuery));
  return new Map(
    names.map((name, index) => {
      const place = results[index] ?? null;
      return [name, namesPlace(name, place) ? place : null];
    })
  );
};

/** An aggregate split by whether each home city resolved to a place we can price. */
interface MatchedSheet {
  aggregate: AttendeeAggregate;
  /** Resolved home cities, spelled as the catalog spells them. */
  origins: Origin[];
  /** The place each resolved home city matched, for its airport. */
  pinned: PlaceOption[];
  /** Entries that match no place ("Remote"), as the sheet spells them. */
  unmatched: Origin[];
}

/** A list of people with no home cities in it: only how many are coming. */
interface HeadcountOnly {
  people: number;
}

/** Resolves an aggregate's home cities, splitting matched from unmatched. */
const matchOrigins = async (aggregate: AttendeeAggregate): Promise<MatchedSheet> => {
  const places = await resolveAll(aggregate.origins.map((origin) => origin.city));
  const matched: MatchedSheet = { aggregate, origins: [], pinned: [], unmatched: [] };
  for (const origin of aggregate.origins) {
    const place = places.get(origin.city);
    if (!place) {
      matched.unmatched.push(origin);
      continue;
    }
    matched.origins.push({ city: place.city, count: origin.count });
    matched.pinned.push(place);
  }
  return matched;
};

/**
 * Reads a sheet on the device. The home-city column is the one a header names; when there is
 * none, or it holds no place we can price (an "Office" column of "Remote"), it is the column whose
 * values are most often places. A sheet with no column of places is a list of people without home
 * cities, read as a headcount.
 * @throws {UploadRefused} When the file is too big or the sheet is empty.
 */
const readSheet = async (file: File): Promise<MatchedSheet | HeadcountOnly> => {
  if (file.size > MAX_ATTENDEE_FILE_BYTES)
    throw new UploadRefused("That file is too big to read here. Keep it under 10 MB.");
  const rows = await readAttendeeRows(file);
  const sampled = await resolveAll(sampleValues(rows, ATTENDEE_COLUMN_SAMPLE));
  const isPlace = (value: string) => Boolean(sampled.get(value));
  const guessed = pickCityColumn(rows, isPlace);
  const guess = guessed ? await matchOrigins(aggregateHomeCities(rows, guessed)) : null;
  if (guess && guess.origins.length > 0) return guess;

  const column = placeColumn(rows, ATTENDEE_COLUMN_SAMPLE, isPlace);
  const picked = column ? await matchOrigins(aggregateHomeCities(rows, column)) : null;
  if (picked && picked.origins.length > 0) return picked;

  const hasHeader = looksLikeHeader(rows);
  const countColumn = hasHeader ? findCountColumn(rows[0]) : -1;
  const people = (hasHeader ? rows.slice(1) : rows).reduce((sum, row) => sum + rowPeople(row, countColumn), 0);
  if (people <= 0) throw new UploadRefused("That sheet is empty.");
  return { people };
};

/**
 * Sends a PDF or Word document for processing: uploaded straight to storage, read there, then
 * shown to the planner to confirm before anything reaches the estimate.
 * @throws {UploadRefused} When the upload is refused, reading fails, or the planner declines.
 */
const readDocument = async (file: File): Promise<MatchedSheet> => {
  const read = await readAttendeeDocument(file);
  if ("error" in read) throw new UploadRefused(read.error);
  const { origins } = read.result;
  if (!origins) throw new UploadRefused("Couldn't read home cities from that file. Try a CSV or Excel list.");
  if (origins.length === 0) throw new UploadRefused("No home cities found in that file.");
  const people = read.result.people ?? origins.reduce((sum, origin) => sum + origin.count, 0);
  const accepted = await askToConfirm(file.name, origins, people);
  if (!accepted) throw new UploadRefused("No home cities added.");
  return matchOrigins({ origins, people, skipped: 0 });
};

/**
 * Reads attendee files and sets the estimate's home cities from them. CSV and Excel are read in
 * the browser, so names never leave the device; PDF and Word are sent for processing and the
 * result is confirmed first. Every value goes through the same place resolver as a pasted list:
 * matched values become home cities (with the airport they resolved to), and people whose value
 * matches no place ("Remote") count as location unknown, named in the toast. A sheet with no home
 * cities in it sets only the headcount.
 * @returns `upload(file)`, resolving to whether home cities were set, and whether a read is running.
 * @throws Never.
 */
export function useAttendeeFile(): { upload: (file: File) => Promise<boolean>; reading: boolean } {
  // Hooks
  const { showToast } = useToast();
  const { edit, workspace } = useEstimator();

  // States
  const [reading, setReading] = useState(false);

  // Handlers
  const upload = useCallback(
    async (file: File) => {
      setReading(true);
      try {
        const list = isAttendeeDocument(file) ? await readDocument(file) : await readSheet(file);
        if (!("origins" in list)) {
          const headcount = Math.min(list.people, MAX_ORIGIN_COUNT);
          // The new list replaces the old one: its home cities and file go, not just the count.
          edit({ type: "setOrigins", origins: [], fileName: file.name }, "user");
          edit({ type: "setHeadcount", headcount }, "user");
          showToast(
            `Read ${headcount.toLocaleString()} people. The list has no home cities, so flights aren't priced yet.`,
            "success"
          );
          return false;
        }
        const { aggregate, origins, pinned, unmatched } = list;
        if (origins.length === 0) {
          showToast("None of the home cities matched a place we can price. Check the city column.", "error");
          return false;
        }
        const unknown = unmatched.reduce((sum, origin) => sum + origin.count, 0);
        edit({ type: "setOrigins", origins, fileName: file.name }, "user");
        pinned.forEach(
          (place) => place.codes[0] && edit({ type: "setAirport", city: place.city, code: place.codes[0] }, "user")
        );
        if (unknown > 0) {
          const total = Math.min(aggregate.people, MAX_ORIGIN_COUNT);
          edit({ type: "setHeadcount", headcount: Math.max(workspace.headcount, total) }, "user");
        }
        const read = `Read ${aggregate.people.toLocaleString()} people in ${origins.length} home ${origins.length === 1 ? "city" : "cities"}.`;
        if (unmatched.length === 0) {
          showToast(read, "success");
        } else {
          const named = unmatched.slice(0, UNMATCHED_NAMED).map((origin) => origin.city);
          const more = unmatched.length - named.length;
          showToast(
            `${read} Couldn't match ${unmatched.length} ${unmatched.length === 1 ? "entry" : "entries"}: ${named.join(", ")}${more ? ` and ${more} more` : ""}. ${unknown.toLocaleString()} counted as location unknown.`,
            "warning"
          );
        }
        return true;
      } catch (error) {
        if (error instanceof UploadRefused) showToast(error.message, "error");
        else showToast("Couldn't read that file. Use CSV, Excel, PDF or Word.", "error");
        return false;
      } finally {
        setReading(false);
      }
    },
    [showToast, edit, workspace.headcount]
  );

  return { upload, reading };
}
